import { SubpageHeader } from "@/components/global/SubpageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/lib/queryClient";
import {
  applySyncedCompanyInformation,
  getCompanyInformation,
  resetConfiguration,
} from "@/services/config.services";
import { fetchPointeuseCompanyProfile } from "@/services/company.service";
import { RefreshCw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

export const SettingsPage = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  /** Incrémenté après une sync réussie pour relire le localStorage à l’écran. */
  const [companyInfoVersion, setCompanyInfoVersion] = useState(0);
  const cardClass = "border rounded-xl p-4 bg-white/80 border-gray-200 backdrop-blur-sm";

  void companyInfoVersion;
  const info = getCompanyInformation();
  const company = {
    name: typeof info.name === "string" ? info.name : undefined,
    siret: typeof info.siret === "string" ? info.siret : undefined,
    address: typeof info.address === "string" ? info.address : undefined,
    postalCode: typeof info.postalCode === "string" ? info.postalCode : undefined,
    city: typeof info.city === "string" ? info.city : undefined,
  };

  const handleRefreshData = async () => {
    setIsSyncing(true);
    try {
      const profile = await fetchPointeuseCompanyProfile();
      applySyncedCompanyInformation(profile);
      setCompanyInfoVersion((v) => v + 1); // Pour relire le localStorage à l’écran.
      await queryClient.invalidateQueries();
      await queryClient.refetchQueries({ queryKey: ["employees"] });
      await queryClient.refetchQueries({ type: "active" });
      toast.success("Synchronisation terminée");
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : "Impossible de synchroniser les données"
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const handleConfirmReconfigure = () => {
    resetConfiguration();
    window.location.href = "/";
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SubpageHeader pageTitle="Paramètres" />
      <div className="min-h-0 flex-1 overflow-y-auto w-full space-y-4 px-6 pt-6 pb-[max(2.5rem,env(safe-area-inset-bottom))]">
      <div className="flex gap-3">
        <div className={`flex flex-col gap-2 w-full ${cardClass}`}>
          <h1 className="text-xl font-semibold">Informations</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs text-muted-foreground">Nom</p>
              <p className="text-sm font-medium text-foreground">
                {company.name || "-"}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs text-muted-foreground">Siret</p>
              <p className="text-sm font-medium text-foreground">
                {company.siret || "-"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs text-muted-foreground">Adresse</p>
              <p className="text-sm font-medium text-foreground">
                {company.address || "-"}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs text-muted-foreground">Code postal</p>
              <p className="text-sm font-medium text-foreground">
                {company.postalCode || "-"}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs text-muted-foreground">Ville</p>
              <p className="text-sm font-medium text-foreground">
                {company.city || "-"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={`flex flex-col gap-3 ${cardClass}`}>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <RefreshCw className="h-5 w-5" /> Synchronisation
        </h1>
        <p className="text-sm text-muted-foreground">
          Rafraichissez les donnees locales de la pointeuse.
        </p>
        <div>
          <Button onClick={handleRefreshData} disabled={isSyncing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
            {isSyncing ? "Synchronisation..." : "Actualiser les donnees"}
          </Button>
        </div>
      </div>

      <div className={`flex flex-col gap-3 ${cardClass}`}>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Settings2 className="h-5 w-5" /> Actions sensibles
        </h1>
        <p className="text-sm text-muted-foreground">
          Cette action deconnecte la pointeuse et necessite une nouvelle configuration.
        </p>
        <div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" type="button">
                Reconfigurer la pointeuse
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Reconfigurer la pointeuse
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Vous serez déconnecté et devrez saisir à nouveau les
                  identifiants de configuration. Voulez-vous continuer ?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel type="button">Annuler</AlertDialogCancel>
                <AlertDialogAction
                  type="button"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleConfirmReconfigure}
                >
                  Reconfigurer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
    </div>
  );
};